import { formatPesoAmount } from '../utils/money';
import React from 'react';
import { FiInfo, FiX } from 'react-icons/fi';
import { normalizeQuantity } from '../utils/quantity';
import { computeFormatOfferSubtotal, getFormatOfferId } from '../utils/formatOffers';

const resolveQuantity = (item, variety, getItemQuantity) => {
  if (typeof getItemQuantity === 'function') {
    return getItemQuantity(item, variety);
  }
  return item?.quantity ?? item?.currentQuantity ?? item?.requestedQuantity ?? 0;
};

export const computeCartNonDiscountableSubtotal = ({
  items = [],
  getVariety,
  getItemQuantity,
  getItemPricing,
} = {}) => items.reduce((sum, item) => {
  const variety = getVariety ? getVariety(item) : null;
  const quantity = normalizeQuantity(resolveQuantity(item, variety, getItemQuantity));
  const pricing = typeof getItemPricing === 'function'
    ? getItemPricing(item, variety, quantity)
    : null;
  const explicitSubtotal = Number(
    pricing?.subtotal ?? item?.subtotal ?? item?.currentSubtotal
  );
  const subtotal = Number.isFinite(explicitSubtotal)
    ? Math.max(Math.floor(explicitSubtotal), 0)
    : getFormatOfferId(item)
      ? computeFormatOfferSubtotal(item?.price, quantity)
      : Math.max(
        Math.floor((Number(item?.price || 0) - Number(item?.discount || 0)) * quantity),
        0
      );
  const eligibleValue = pricing?.orderDiscountEligibleSubtotal
    ?? item?.orderDiscountEligibleSubtotal
    ?? item?.promotionSnapshot?.orderDiscountEligibleSubtotal
    ?? item?.promotionSnapshot?.eligibleSubtotal;
  const parsedEligible = Number(eligibleValue);
  const hasExplicitEligible = eligibleValue !== null
    && eligibleValue !== undefined
    && eligibleValue !== '';
  const eligibleSubtotal = getFormatOfferId(item)
    ? 0
    : hasExplicitEligible && Number.isFinite(parsedEligible)
      ? Math.min(Math.max(Math.floor(parsedEligible), 0), subtotal)
      : subtotal;
  return sum + Math.max(subtotal - eligibleSubtotal, 0);
}, 0);

const resolveDiscountAmount = ({
  itemsSubtotal,
  nonDiscountableSubtotal,
  orderDiscountPercentage,
  orderDiscountAmount,
}) => {
  const subtotal = Math.max(Number(itemsSubtotal || 0) - nonDiscountableSubtotal, 0);
  const percent = orderDiscountPercentage === null || orderDiscountPercentage === undefined
    ? null
    : Number(orderDiscountPercentage);
  if (Number.isFinite(percent)) {
    return Math.min(Math.max(Math.floor(subtotal * (percent / 100)), 0), subtotal);
  }
  const amount = Number(orderDiscountAmount);
  return Math.min(Number.isFinite(amount) ? Math.max(Math.floor(amount), 0) : 0, subtotal);
};

const formatQuantity = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0';
  const hasDecimals = Math.abs(amount - Math.round(amount)) > 0.0001;
  return amount.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 3 }
    : undefined);
};

const formatMoney = formatPesoAmount;

const normalizeLabel = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const buildItemDisplayName = (item, variety) => {
  const productName = normalizeLabel(item?.productName || variety?.productName || variety?.Product?.productName);
  const varietyName = normalizeLabel(item?.varietyName || variety?.varietyName);
  if (productName && varietyName) {
    if (productName.toLowerCase() === varietyName.toLowerCase()) {
      return productName;
    }
    return `${productName} - ${varietyName}`;
  }
  return productName || varietyName || 'Producto';
};

const renderValidationError = (validationError) => {
  if (!validationError) return null;
  return (
    <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">
      <div className="flex items-center gap-1 font-semibold">
        <FiInfo size={13} />
        <span>{validationError.title || 'Revisar línea'}</span>
      </div>
      {validationError.detail && (
        <p className="mt-1 leading-snug text-red-700">{validationError.detail}</p>
      )}
    </div>
  );
};

const CartPanel = ({
  items = [],
  getVariety,
  getItemQuantity,
  onItemClick,
  onRemoveItem,
  canEdit = true,
  headerContent,
  panelClassName = 'bg-white shadow-lg rounded-lg p-4',
  footerClassName = 'flex flex-col gap-1 mt-4 bottom-0',
  itemsSubtotal = 0,
  orderDiscountPercentage,
  orderDiscountAmount,
  totalAmount = 0,
  roundingDiscountAmount = 0,
  roundingPendingMethod = false,
  footerActions,
  pendingItemDiscounts = {},
  pendingOrderDiscount = null,
  onResumePendingItemDiscount,
  onResumePendingOrderDiscount,
  canOverridePriceType = false,
  getItemPriceTypeOptions,
  onItemPriceTypeChange,
  getItemPricing,
  getItemValidationError,
  showSummaryBreakdown = true,
  showPrices = true,
  showTotal = showPrices,
  showClientPricingCategoryLabels = false,
}) => {
  const nonDiscountableSubtotal = computeCartNonDiscountableSubtotal({
    items,
    getVariety,
    getItemQuantity,
    getItemPricing,
  });
  const effectiveOrderDiscount = resolveDiscountAmount({
    itemsSubtotal,
    nonDiscountableSubtotal,
    orderDiscountPercentage,
    orderDiscountAmount,
  });

  const handleItemClick = (item, variety) => {
    if (!onItemClick) return;
    onItemClick(item, variety);
  };

  return (
    <>
      <div className={panelClassName}>
        {headerContent && <div className="sticky top-0 bg-white pb-3 z-10">{headerContent}</div>}
        {items.length > 0 &&
          items.map((item, index) => {
            const variety = getVariety ? getVariety(item) : null;
            const sourcePriceIndex = Number.isInteger(item?.sourcePriceIndex)
              ? item.sourcePriceIndex
              : item.priceIndex;
            const displayPriceIndex = Number.isInteger(item?.priceIndex)
              ? item.priceIndex
              : sourcePriceIndex;
            const imageSrc =
              item?.priceImage ||
              item?.varietyImage ||
              item?.productImage ||
              variety?.VarietyPrices?.[displayPriceIndex]?.priceImage ||
              variety?.varietyImage ||
              'https://placehold.co/50';
            const itemQuantity = normalizeQuantity(
              resolveQuantity(item, variety, getItemQuantity)
            );
            const itemPrice = item.price ?? 0;
            const hasFormatOffer = Boolean(getFormatOfferId(item));
            const itemDiscount = hasFormatOffer ? 0 : (item.discount ?? 0);
            const discountedPrice = itemPrice - itemDiscount;
            const pricing = typeof getItemPricing === 'function'
              ? getItemPricing(item, variety, itemQuantity)
              : null;
            const displayItemPrice = Number.isFinite(Number(pricing?.unitPrice))
              ? Number(pricing.unitPrice)
              : itemPrice;
            const hasTemporaryPrice = item?.promotionSnapshot?.kind === 'temporary_price';
            const hasPromotion = item?.discountSource === 'promotion'
              && Number(item?.promotionDiscountAmount || 0) > 0;
            const displayDiscountedPrice = Number.isFinite(Number(pricing?.discountedUnitPrice))
              ? Number(pricing.discountedUnitPrice)
              : hasPromotion && itemQuantity > 0 && Number.isFinite(Number(item?.subtotal))
                ? Number(item.subtotal) / itemQuantity
                : discountedPrice;
            const displaySubtotal = Number.isFinite(Number(pricing?.subtotal))
              ? Number(pricing.subtotal)
              : Number.isFinite(Number(item?.subtotal))
                ? Number(item.subtotal)
                : hasFormatOffer
                  ? computeFormatOfferSubtotal(displayDiscountedPrice, itemQuantity)
                  : (displayDiscountedPrice * itemQuantity);
            const hasDiscount = Math.abs(displayItemPrice - displayDiscountedPrice) > 0.0001;
            const taxFreeApplied = Boolean(pricing?.taxFreeApplied);
            const itemDescription =
              variety?.VarietyPrices?.[displayPriceIndex]?.description
              || item?.description
              || item?.metricUnit
              || 'unidad';
            const itemDisplayName = buildItemDisplayName(item, variety);
            const pendingKey = `${item.varietyId}-${sourcePriceIndex}`;
            const pendingEntry = pendingItemDiscounts?.[pendingKey] || null;
            const priceTypeOptions = typeof getItemPriceTypeOptions === 'function'
              ? getItemPriceTypeOptions(item, variety)
              : [];
            const canEditPriceType = canOverridePriceType
              && !hasFormatOffer
              && typeof onItemPriceTypeChange === 'function'
              && Array.isArray(priceTypeOptions)
              && priceTypeOptions.length > 0;
            const priceTypeValue = item?.priceTypeOverride
              ? (item?.priceType || '')
              : '';
            const validationError = typeof getItemValidationError === 'function'
              ? getItemValidationError(item, {
                variety,
                sourcePriceIndex,
                displayPriceIndex,
                index,
              })
              : null;
            const hasValidationError = Boolean(validationError);

            return (
              <div
                key={`${item.varietyId}-${sourcePriceIndex}-${index}`}
                className={`relative border rounded-md shadow-sm px-3 py-2 cursor-pointer mb-2 ${
                  hasValidationError
                    ? 'bg-red-50 border-red-300 ring-1 ring-red-200 hover:bg-red-50'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => handleItemClick(item, variety)}
              >
                {canEdit && onRemoveItem && (
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveItem(item);
                    }}
                    className="absolute top-2 right-2 text-white bg-red-500 hover:bg-red-600 rounded-full p-1"
                  >
                    <FiX size={14} />
                  </button>
                )}
                <div className="flex items-center gap-3 pr-8">
                  <img
                    src={imageSrc}
                    alt={itemDisplayName}
                    className="w-12 h-12 rounded object-cover flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-1.5">
                          <div className="min-w-0 truncate text-sm font-semibold leading-snug">
                            {itemDisplayName}
                          </div>
                          {hasValidationError && (
                            <div className="group relative inline-flex shrink-0">
                              <button
                                type="button"
                                className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-red-700 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-300"
                                aria-label="Ver detalle del error de descuento"
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                              >
                                <FiInfo size={13} />
                              </button>
                              <div className="pointer-events-none absolute right-0 top-full z-30 mt-2 hidden w-72 rounded-md border border-red-200 bg-white p-3 text-left text-xs leading-snug text-gray-700 shadow-lg group-hover:block group-focus-within:block">
                                <div className="font-semibold text-red-700">
                                  {validationError.title || 'Revisar línea'}
                                </div>
                                {validationError.detail && (
                                  <p className="mt-1">{validationError.detail}</p>
                                )}
                                {validationError.reason && (
                                  <p className="mt-2 text-gray-600">{validationError.reason}</p>
                                )}
                                {validationError.fix && (
                                  <p className="mt-2 font-medium text-gray-800">{validationError.fix}</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 leading-snug mt-0.5 truncate">
                          {itemDescription}
                        </div>
                        {item.isSoldOut && <div className="mt-1 text-xs font-semibold text-amber-700">No disponible · conservado en la venta</div>}
                        {showClientPricingCategoryLabels && item?.discountSource === 'client_category' && item?.clientPricingCategoryName && (
                          <div className="mt-1 truncate text-[11px] font-semibold text-emerald-700">
                            Descuento: {item.clientPricingCategoryName}
                          </div>
                        )}
                      </div>

                      <div className={`${showPrices ? 'w-[118px]' : ''} shrink-0 text-right`}>
                        <span className="inline-flex items-center justify-center rounded bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
                          Cant: {formatQuantity(itemQuantity)}
                        </span>
                        {showPrices && (
                          <div className="mt-1">
                            <div className="truncate text-[11px] leading-tight text-gray-500">
                              {hasDiscount ? (
                                <>
                                  <span className="line-through mr-1">
                                    ${formatMoney(displayItemPrice)}
                                  </span>
                                  <span className="text-gray-700">
                                    ${formatMoney(displayDiscountedPrice)}
                                  </span>
                                  <span className="ml-1 text-red-600">
                                    ({displayItemPrice > 0 ? `-${Math.round(((displayItemPrice - displayDiscountedPrice) / displayItemPrice) * 100)}%` : '-0%'})
                                  </span>
                                </>
                              ) : (
                                `Precio: $${formatMoney(displayItemPrice)}`
                              )}
                              {taxFreeApplied && (
                                <span
                                  className="ml-1 inline-flex items-center text-blue-600 align-middle"
                                  title="Precio ajustado sin impuestos excluidos."
                                  aria-label="Precio ajustado sin impuestos excluidos."
                                >
                                  <FiInfo size={12} />
                                </span>
                              )}
                            </div>
                            <div className="text-sm font-semibold leading-tight text-gray-900">
                              ${formatMoney(displaySubtotal)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {canEditPriceType && (
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs text-gray-600">Tipo precio</label>
                    <select
                      className="border border-gray-300 rounded px-2 py-1 text-xs"
                      value={priceTypeValue}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        event.stopPropagation();
                        onItemPriceTypeChange(item, event.target.value || '');
                      }}
                    >
                      <option value="">Auto</option>
                      {priceTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {item?.priceTypeOverride && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        Manual
                      </span>
                    )}
                  </div>
                )}
                {hasFormatOffer && (
                  <div className="mt-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                    Oferta · sin descuentos adicionales
                  </div>
                )}
                {(hasPromotion || hasTemporaryPrice) && (
                  <div className="mt-2 inline-flex max-w-full items-center rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 border border-emerald-100">
                    <span className="truncate">
                      {item.promotionSnapshot?.kind === 'temporary_price' ? 'Temporal: ' : 'Promo: '}{item.promotionName || 'Promocion'}
                    </span>
                  </div>
                )}
                {item.promotionSnapshot?.weightPriceId && <p className="mt-1 text-xs text-emerald-700">
                  Precio promocional aproximado: ${formatPesoAmount(item.promotionSnapshot.weightUnitPrice)} / kg, aplicado a todo el peso.
                </p>}
                {pendingEntry && (
                  <div className="mt-2 flex items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
                    <span>Descuento pendiente</span>
                    {onResumePendingItemDiscount && (
                      <button
                        type="button"
                        className="text-amber-800 underline"
                        onClick={(event) => {
                          event.stopPropagation();
                          onResumePendingItemDiscount(pendingEntry);
                        }}
                      >
                        Continuar
                      </button>
                    )}
                  </div>
                )}
                {renderValidationError(validationError)}
              </div>
            );
          })}
      </div>
      <div className={footerClassName}>
        {showSummaryBreakdown && (
          <>
            <div className="flex justify-between items-center text-sm">
              <span className="font-medium">Subtotal productos:</span>
              <span>${formatPesoAmount(Number(itemsSubtotal || 0))}</span>
            </div>
            {effectiveOrderDiscount > 0 && (
              <div className="flex justify-between items-center text-sm text-red-600">
                <span className="font-medium">
                  Descuento orden
                  {orderDiscountPercentage != null ? ` (${orderDiscountPercentage}%)` : ''}:
                </span>
                <span>- ${formatPesoAmount(effectiveOrderDiscount)}</span>
              </div>
            )}
            {pendingOrderDiscount && (
              <div className="flex items-center justify-between gap-2 text-xs text-amber-700 border border-amber-200 bg-amber-50 rounded px-2 py-1">
                <span>Descuento de orden pendiente</span>
                {onResumePendingOrderDiscount && (
                  <button
                    type="button"
                    className="text-amber-800 underline"
                    onClick={() => onResumePendingOrderDiscount(pendingOrderDiscount)}
                  >
                    Continuar
                  </button>
                )}
              </div>
            )}
          </>
        )}
        {Number(roundingDiscountAmount) > 0 && (
          <div className="flex justify-between items-center text-sm text-red-600">
            <span>Descuento por redondeo:</span>
            <span>- ${formatPesoAmount(Number(roundingDiscountAmount))}</span>
          </div>
        )}
        {roundingPendingMethod && <p className="text-xs text-gray-500">Redondeo según método de pago.</p>}
        {showTotal && (
          <div className="flex justify-between items-center mt-1">
            <span className="text-sm font-bold">Total:</span>
            <span className="text-md font-bold text-blue-500">
              ${formatPesoAmount(Number(totalAmount || 0))}
            </span>
          </div>
        )}
        {footerActions}
      </div>
    </>
  );
};

export default CartPanel;
